import { useMemo } from 'react';
import type { CatalogSku } from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';

/**
 * Pricing table — DESIGN.md §6.7, rendered from the SKU market since
 * epic #898 P6 (#918). One rounded-3xl table, one group per namespace
 * (namespace header row + per-SKU input/output row pairs), fixed four
 * columns: model · dimension · official price · effective price. The
 * channel-era per-channel price columns (and the "max channels" column
 * padding they required) are gone — each SKU carries exactly one
 * `rate_multiplier`, shown as a per-row discount tag when it undercuts
 * the list price.
 */

function fmtCNY(usd: number, mult: number, fx: number): string {
  return `¥${(usd * mult * fx).toFixed(2)}`;
}

function fmtDiscount(mult: number): string {
  const zhe = mult * 10;
  if (Number.isInteger(zhe)) return `${zhe}折`;
  return `${zhe.toFixed(1)}折`;
}

/** A catalog SKU narrowed to the rows the table can price. */
interface PricedSku {
  readonly skuId: string;
  readonly displayName: string;
  readonly rateMultiplier: number;
  readonly inputPerMtokUsd: number;
  readonly outputPerMtokUsd: number;
  readonly cacheReadPerMtokUsd: number | null;
  readonly cacheWritePerMtokUsd: number | null;
  readonly supportsPromptCache: boolean;
}

interface NamespaceGroup {
  readonly namespaceSlug: string;
  readonly namespaceDisplayName: string;
  readonly skus: readonly PricedSku[];
}

interface PriceDimension {
  readonly id: string;
  readonly label: string;
  readonly value: number | null;
}

function dimensionsForSku(
  sku: PricedSku,
  labels: { input: string; cacheRead: string; cacheWrite: string; output: string },
): readonly PriceDimension[] {
  return [
    { id: 'in', label: labels.input, value: sku.inputPerMtokUsd },
    ...(sku.supportsPromptCache
      ? [
          { id: 'cache-read', label: labels.cacheRead, value: sku.cacheReadPerMtokUsd },
          { id: 'cache-write', label: labels.cacheWrite, value: sku.cacheWritePerMtokUsd },
        ]
      : []),
    { id: 'out', label: labels.output, value: sku.outputPerMtokUsd },
  ];
}

function buildGroups(skus: readonly CatalogSku[]): readonly NamespaceGroup[] {
  const map = new Map<string, { displayName: string; slug: string; skus: PricedSku[] }>();
  for (const sku of skus) {
    if (sku.input_per_mtok_usd === null || sku.output_per_mtok_usd === null) continue;
    let entry = map.get(sku.namespace_id);
    if (!entry) {
      entry = {
        displayName: sku.namespace_display_name,
        slug: sku.namespace_slug,
        skus: [],
      };
      map.set(sku.namespace_id, entry);
    }
    entry.skus.push({
      skuId: sku.sku_id,
      displayName: sku.model_display_name ?? sku.model,
      rateMultiplier: sku.rate_multiplier,
      inputPerMtokUsd: sku.input_per_mtok_usd,
      outputPerMtokUsd: sku.output_per_mtok_usd,
      cacheReadPerMtokUsd: sku.cache_read_per_mtok_usd,
      cacheWritePerMtokUsd: sku.cache_write_per_mtok_usd,
      supportsPromptCache: sku.supports_prompt_cache,
    });
  }
  const groups: NamespaceGroup[] = [];
  for (const entry of map.values()) {
    groups.push({
      namespaceSlug: entry.slug,
      namespaceDisplayName: entry.displayName,
      skus: entry.skus,
    });
  }
  return groups;
}

interface Props {
  /** `null` while the catalog request is in flight (skeleton). */
  readonly catalogSkus: readonly CatalogSku[] | null;
  readonly fxRate: number;
}

export function Pricing({ catalogSkus, fxRate }: Props): JSX.Element {
  const t = useT();
  const groups = useMemo(() => buildGroups(catalogSkus ?? []), [catalogSkus]);

  return (
    <section id="pricing" className="bg-butter/40 py-28" data-testid="pricing">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-14 max-w-3xl">
          <p className="mb-4 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-claude">
            {t('pricing.eyebrow')}
          </p>
          <h2 className="text-[40px] font-semibold leading-[1.05] text-ink md:text-[52px]">
            <span className="serif text-claude">{t('pricing.title.accent')}</span>
            {t('pricing.title.trail')}
          </h2>
          <p className="mt-5 text-[16px] text-ink-muted">{t('pricing.subtitle')}</p>
        </div>

        <div className="ghost overflow-hidden rounded-3xl bg-linen">
          {catalogSkus === null ? (
            <div className="h-40 animate-pulse rounded-3xl bg-butter/50" />
          ) : groups.length === 0 ? (
            <p
              className="px-6 py-12 text-center font-mono text-[12.5px] text-ink-subtle"
              data-testid="pricing-empty"
            >
              {t('pricing.empty')}
            </p>
          ) : (
            <>
              <div className="divide-y divide-cream md:hidden" data-testid="pricing-mobile">
                {groups.map((group) => (
                  <section key={group.namespaceSlug} className="px-4 py-5">
                    <div className="mb-4 flex items-baseline justify-between gap-3">
                      <h3 className="m-0 text-[15px] font-semibold text-ink">
                        {group.namespaceDisplayName}
                      </h3>
                      <span className="font-mono text-[11px] text-ink-subtle">
                        {t('pricing.group.sku_count', { count: String(group.skus.length) })}
                      </span>
                    </div>
                    <div className="flex flex-col gap-5">
                      {group.skus.map((sku) => (
                        <article key={sku.skuId} className="rounded-2xl bg-butter/40 p-4">
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <h4 className="m-0 text-[14px] font-semibold text-ink">
                              {sku.displayName}
                            </h4>
                            {sku.rateMultiplier < 1 ? (
                              <span className="font-mono text-[10px] font-semibold text-claude">
                                {fmtDiscount(sku.rateMultiplier)}
                              </span>
                            ) : null}
                          </div>
                          <dl className="m-0 flex flex-col gap-3">
                            {dimensionsForSku(sku, {
                              input: t('pricing.row.input'),
                              cacheRead: t('pricing.row.cache_read'),
                              cacheWrite: t('pricing.row.cache_write'),
                              output: t('pricing.row.output'),
                            }).map((dimension) => (
                              <div
                                key={dimension.id}
                                className="grid grid-cols-2 gap-x-4 gap-y-1 ghost-t pt-3"
                              >
                                <dt className="col-span-2 text-[11px] text-ink-subtle">
                                  {dimension.label}
                                </dt>
                                <dd className="m-0 min-w-0">
                                  <span className="block font-mono text-[10px] text-ink-subtle">
                                    {t('pricing.col.official')}
                                  </span>
                                  <span className="block font-mono text-[13px] text-ink-subtle">
                                    {dimension.value === null || dimension.value <= 0
                                      ? t('pricing.row.not_billed')
                                      : fmtCNY(dimension.value, 1, fxRate)}
                                  </span>
                                </dd>
                                <dd className="m-0 min-w-0 text-right">
                                  <span className="block font-mono text-[10px] text-ink-subtle">
                                    {t('pricing.col.ours')}
                                  </span>
                                  <span className="block font-mono text-[13px] text-ink">
                                    {dimension.value === null || dimension.value <= 0
                                      ? t('pricing.row.not_billed')
                                      : fmtCNY(dimension.value, sku.rateMultiplier, fxRate)}
                                  </span>
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </article>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
              <div className="hidden md:block" data-testid="pricing-desktop">
                <table className="w-full table-fixed text-left">
                  <colgroup>
                    <col className="w-[200px]" />
                    <col className="w-[110px]" />
                    <col />
                    <col />
                  </colgroup>
                  <tbody className="text-[13px]">
                    {groups.map((group) => [
                      <tr key={`hdr-${group.namespaceSlug}`} className="bg-butter/40">
                        <td colSpan={4} className="px-6 py-2.5">
                          <div className="flex items-baseline justify-between gap-4">
                            <span className="text-[13px] font-semibold text-ink">
                              {group.namespaceDisplayName}
                            </span>
                            <span className="font-mono text-[11px] text-ink-subtle">
                              {t('pricing.group.sku_count', {
                                count: String(group.skus.length),
                              })}
                            </span>
                          </div>
                        </td>
                      </tr>,
                      <tr
                        key={`cols-${group.namespaceSlug}`}
                        className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink-subtle"
                      >
                        <th className="px-6 py-3 font-medium">{t('pricing.col.model')}</th>
                        <th className="px-2 py-3 font-medium" />
                        <th className="px-5 py-3 text-right font-medium">
                          {t('pricing.col.official')}
                        </th>
                        <th className="px-5 py-3 text-right font-medium">
                          {t('pricing.col.ours')}
                        </th>
                      </tr>,
                      ...group.skus.flatMap((sku) => {
                        const dimensions = dimensionsForSku(sku, {
                          input: t('pricing.row.input'),
                          cacheRead: t('pricing.row.cache_read'),
                          cacheWrite: t('pricing.row.cache_write'),
                          output: t('pricing.row.output'),
                        });
                        return dimensions.map((dimension, index) => (
                          <tr key={`${sku.skuId}-${dimension.id}`} className="ghost-t">
                            {index === 0 ? (
                              <td
                                className="px-6 pt-3 pb-0.5 font-medium text-ink"
                                rowSpan={dimensions.length}
                              >
                                <div>{sku.displayName}</div>
                                {sku.rateMultiplier < 1 && (
                                  <div className="mt-0.5 font-mono text-[10px] font-semibold text-claude">
                                    {fmtDiscount(sku.rateMultiplier)}
                                  </div>
                                )}
                              </td>
                            ) : null}
                            <td className="px-2 py-1.5 text-[11px] text-ink-subtle whitespace-nowrap">
                              {dimension.label}
                            </td>
                            <td className="px-5 py-1.5 text-right font-mono text-ink-subtle">
                              {dimension.value === null || dimension.value <= 0
                                ? t('pricing.row.not_billed')
                                : fmtCNY(dimension.value, 1, fxRate)}
                            </td>
                            <td className="px-5 py-1.5 text-right font-mono text-ink">
                              {dimension.value === null || dimension.value <= 0
                                ? t('pricing.row.not_billed')
                                : fmtCNY(dimension.value, sku.rateMultiplier, fxRate)}
                            </td>
                          </tr>
                        ));
                      }),
                    ])}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <p className="mt-5 font-mono text-[12.5px] text-ink-subtle">{t('pricing.footnote')}</p>
      </div>
    </section>
  );
}
